import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { ArrowCircleUpRight } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CarouselNav, CarouselDots } from '@/components/ds';
import LearnArticleCard from './LearnArticleCard';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { useCarouselTracking } from '@/hooks/useCarouselTracking';

interface ArticleItem {
  id: string;
  slug: string;
  title: string;
  cover_image: string | null;
  topic_name?: string;
  topic_color?: string | null;
}

interface LearnCarouselSectionProps {
  id?: string;
  title: string;
  subtitle?: string | null;
  articles: ArticleItem[];
  variant?: "featured" | "feed";
  onSeeMore?: () => void;
  className?: string;
  isLoading?: boolean;
  boldTitle?: boolean;
  /** ⚠️ ANALYTICS: Section position for visibility tracking */
  position?: number;
}

export default function LearnCarouselSection({
  id,
  title,
  subtitle,
  articles,
  variant = "feed",
  onSeeMore,
  className,
  isLoading,
  boldTitle = false,
  position = 1,
}: LearnCarouselSectionProps) {
  // Only show "See more" button if 5+ articles
  const showSeeMore = onSeeMore && articles.length >= 5;

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef, inView: sectionInView } = useSectionVisibility({
    sectionId: id || 'learn-carousel',
    sectionName: title,
    pageName: 'Learn',
    position,
  });

  // ⚠️ ANALYTICS: Carousel tracking
  const { trackCarouselScrolled } = useCarouselTracking({
    carouselId: id || 'learn-carousel',
    section: id || 'learn',
    pageName: 'Learn',
    totalItems: articles?.length || 0,
  });

  const prevIndexRef = useRef(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [showNavButtons, setShowNavButtons] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();

    // ⚠️ ANALYTICS: Track carousel scroll
    if (newIndex !== prevIndexRef.current) {
      trackCarouselScrolled(prevIndexRef.current, newIndex, 'drag');
      prevIndexRef.current = newIndex;
    }

    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(newIndex);
  }, [emblaApi, trackCarouselScrolled]);

  useEffect(() => {
    if (!emblaApi) return;
    
    // Check if navigation is needed (more slides than fit in viewport)
    const checkNavVisibility = () => {
      const scrollSnaps = emblaApi.scrollSnapList();
      setShowNavButtons(scrollSnaps.length > 1);
    };
    
    onSelect();
    checkNavVisibility();
    
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', () => {
      onSelect();
      checkNavVisibility();
    });
    
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Loading skeleton
  if (isLoading) {
    return (
      <section className={cn("bg-background py-16 md:py-24 overflow-hidden", className)}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="mb-8">
            <div className="h-12 w-64 bg-muted animate-pulse rounded" />
          </div>
        </div>
        <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
          <div className="flex gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-[435px] flex-shrink-0">
                <div className="w-[435px] h-[435px] bg-muted animate-pulse rounded-3xl mb-4" />
                <div className="h-6 w-3/4 bg-muted animate-pulse rounded mb-3" />
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-muted animate-pulse rounded-full" />
                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <section id={id} ref={sectionRef} className={cn("bg-background py-12 md:py-16 lg:py-24 overflow-hidden scroll-mt-24", className)}>
      {/* molecule-section-header-minimal: Title with inline nav */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          {/* Left: Title and Subtitle */}
          <div className="flex flex-col gap-2">
            {/* atom-text-section-header-display: Responsive sizing */}
            <h2 className="text-32 md:text-56 font-semibold text-foreground">
              {title}
            </h2>
            {/* atom-text-section-subheader */}
            {subtitle && (
              <p className="text-14 md:text-17 text-muted-foreground max-w-2xl">
                {subtitle}
              </p>
            )}
          </div>

          {/* Right: Desktop nav arrows + See More Button inline with header */}
          <div className="hidden md:flex items-end gap-4">
            {showNavButtons && (
              <CarouselNav
                onPrev={scrollPrev}
                onNext={scrollNext}
                canScrollPrev={canScrollPrev}
                canScrollNext={canScrollNext}
                variant="subdued"
              />
            )}
            {showSeeMore && (
              <Button 
                size="sm" 
                onClick={onSeeMore}
              >
                See more
                <ArrowCircleUpRight weight="fill" className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
        <div className="overflow-visible" ref={emblaRef}>
          <div className="flex gap-6 embla-gesture-lock">
            {articles.map((article, index) => (
              <div
                key={article.id}
                className={
                  index === articles.length - 1
                    ? "mr-4 md:mr-8 lg:mr-[max(2rem,calc((100vw-1400px)/2+2rem))]"
                    : ""
                }
              >
                <LearnArticleCard
                  slug={article.slug}
                  title={article.title}
                  coverImage={article.cover_image}
                  topicName={article.topic_name}
                  topicColor={article.topic_color}
                  variant={variant}
                  index={index}
                  sectionId={id || 'learn-carousel'}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile navigation - centered dots only */}
      <div className={`max-w-[1400px] mx-auto px-4 md:px-8 md:hidden ${variant === 'featured' ? 'mt-10' : 'mt-6'}`}>
        <div className="flex justify-center">
          <CarouselDots
            selectedIndex={selectedIndex}
            totalSlides={articles.length}
            onDotClick={scrollTo}
          />
        </div>
      </div>

      {/* Mobile See More Button */}
      {showSeeMore && (
        <div className="md:hidden max-w-[1400px] mx-auto px-4 mt-6">
          <div className="flex justify-end">
            <Button 
              size="sm" 
              onClick={onSeeMore}
            >
              See more
              <ArrowCircleUpRight weight="fill" className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
